import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { guardarDestino } from '@/lib/destino';
import CampoTexto from '@/components/base/CampoTexto';
import CampoDinheiro from '@/components/base/CampoDinheiro';
import CampoArea from '@/components/base/CampoArea';
import { UFS } from '@/pages/assistente-evento/constants';
import CaixaRoxa from './components/CaixaRoxa';
import BlocoPremios from './components/BlocoPremios';
import {
  REGULAMENTO_PADRAO,
  dataHoraSorteioISO,
  formatarCentavos,
  mascaraCNPJ,
  novoPremio,
} from './utils';
import type { EventoOpcao, ModoRifa, PremioDraft, SorteioPor } from './types';

const inputCls =
  'mt-1 w-full rounded-md border bg-white px-3 py-2 text-[15px] text-foreground-950 outline-none transition-colors focus:border-primary-500 border-secondary-300';

const OPCOES_SORTEIO: { valor: SorteioPor; rotulo: string }[] = [
  { valor: 'instagram', rotulo: 'Ao vivo no Instagram' },
  { valor: 'facebook', rotulo: 'Ao vivo no Facebook' },
  { valor: 'loteria_federal', rotulo: 'Loteria Federal' },
];

export default function RifaNovaPage() {
  const navigate = useNavigate();
  const { user, pronto } = useAuth();
  const [searchParams] = useSearchParams();
  const eventoDaUrl = Number(searchParams.get('evento')) || null;

  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [modo, setModo] = useState<ModoRifa>(eventoDaUrl ? 'evento' : 'evento');
  const [eventoId, setEventoId] = useState<number | null>(eventoDaUrl);
  const [eventos, setEventos] = useState<EventoOpcao[]>([]);
  const [eventoNome, setEventoNome] = useState('');
  const [inscritos, setInscritos] = useState(0);
  const [valorInscricaoCentavos, setValorInscricaoCentavos] = useState(0);

  const [nomeRifa, setNomeRifa] = useState('');
  const [metaCentavos, setMetaCentavos] = useState(0);
  const [cidade, setCidade] = useState('');
  const [uf, setUf] = useState('');
  const [descricao, setDescricao] = useState('');

  const [premios, setPremios] = useState<PremioDraft[]>([novoPremio()]);

  const [sorteioData, setSorteioData] = useState('');
  const [sorteioHora, setSorteioHora] = useState('');
  const [sorteioPor, setSorteioPor] = useState<SorteioPor>('instagram');
  const [sorteioOnde, setSorteioOnde] = useState('');

  const [valorCentavos, setValorCentavos] = useState(0);
  const [porTalao, setPorTalao] = useState('20');
  const [maxCompra, setMaxCompra] = useState('10');
  const [limite, setLimite] = useState('');
  const [quemVende, setQuemVende] = useState<'qualquer' | 'aprovar'>('qualquer');
  const [absorvoATaxa, setAbsorvoATaxa] = useState(true);

  const [entidadeNome, setEntidadeNome] = useState('');
  const [entidadeCnpj, setEntidadeCnpj] = useState('');
  const [autorizacaoSpa, setAutorizacaoSpa] = useState('');
  const [regulamento, setRegulamento] = useState(REGULAMENTO_PADRAO);

  const [salvando, setSalvando] = useState(false);
  const [rifaId, setRifaId] = useState<number | null>(() => {
    const guardado = localStorage.getItem('rascunho:rifa');
    const n = guardado ? Number(guardado) : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
  });
  const [avisoSalvo, setAvisoSalvo] = useState(false);

  // Carga inicial: sessão + lista de eventos elegíveis.
  useEffect(() => {
    let ativo = true;

    async function carregar() {
      setCarregando(true);
      setErro(null);
      try {
        if (!pronto) return;
        if (!user) {
          const destino = eventoDaUrl ? `/sorteio/nova?evento=${eventoDaUrl}` : '/sorteio/nova';
          guardarDestino(destino);
          navigate(`/entrar?voltar=${encodeURIComponent(destino)}`, { replace: true });
          return;
        }

        const { data: idsData, error: errIds } = await supabase.rpc('meus_eventos');
        if (errIds) throw errIds;
        const ids = ((idsData as unknown[]) || [])
          .map((n) => Number(n))
          .filter((n) => Number.isFinite(n) && n > 0);

        let lista: EventoOpcao[] = [];
        if (ids.length > 0) {
          const { data, error: errEv } = await supabase
            .from('eventos')
            .select('id, nome, fim_em')
            .in('id', ids)
            .eq('vende_ingressos', false)
            .gte('fim_em', new Date().toISOString());
          if (errEv) throw errEv;
          lista = (data as EventoOpcao[]) || [];
        }

        if (ativo) setEventos(lista);

        if (eventoDaUrl && ativo) {
          await carregarDadosEvento(eventoDaUrl);
        }
      } catch (e) {
        if (ativo) {
          setErro(
            mensagemAmigavel(e, 'Não foi possível carregar. Tente novamente.'),
          );
        }
      } finally {
        if (ativo) setCarregando(false);
      }
    }

    carregar();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pronto, user]);

  async function carregarDadosEvento(id: number) {
    const [resInsc, resMod, resEv] = await Promise.all([
      supabase
        .from('inscricoes')
        .select('id', { count: 'exact', head: true })
        .eq('evento_id', id)
        .neq('status', 'cancelada'),
      supabase
        .from('modalidades')
        .select('valor_centavos')
        .eq('evento_id', id)
        .order('ordem')
        .limit(1)
        .maybeSingle(),
      supabase.from('eventos').select('nome').eq('id', id).maybeSingle(),
    ]);
    if (resInsc.error) throw resInsc.error;
    if (resMod.error) throw resMod.error;
    if (resEv.error) throw resEv.error;
    setInscritos(resInsc.count ?? 0);
    setValorInscricaoCentavos(
      (resMod.data as { valor_centavos: number } | null)?.valor_centavos ?? 0,
    );
    setEventoNome((resEv.data as { nome: string } | null)?.nome ?? '');
  }

  async function aoEscolherEvento(idStr: string) {
    const id = Number(idStr) || null;
    setEventoId(id);
    if (id) {
      try {
        await carregarDadosEvento(id);
      } catch {
        setInscritos(0);
        setValorInscricaoCentavos(0);
      }
    } else {
      setInscritos(0);
      setValorInscricaoCentavos(0);
    }
  }

  function sair() {
    if (eventoDaUrl) navigate(`/gerenciar/${eventoDaUrl}`);
    else navigate('/meus-eventos');
  }

  // Upload das fotos de prêmios e montagem do payload de prêmios.
  async function processarPremios(): Promise<(string | null)[]> {
    const uid = user?.id || 'anonimo';
    const urls: (string | null)[] = [];
    let idx = 0;

    for (const p of premios) {
      if (p.foto) {
        const ext = (p.foto.name.split('.').pop() || 'jpg').toLowerCase();
        const caminho = `${uid}/rifa-premio-${Date.now()}-${idx}.${ext}`;
        const { error } = await supabase.storage.from('capas').upload(caminho, p.foto);
        if (!error) {
          const { data } = supabase.storage.from('capas').getPublicUrl(caminho);
          urls.push(data.publicUrl);
        } else {
          urls.push(null);
        }
      } else {
        urls.push(null);
      }
      idx += 1;
    }
    return urls;
  }

  async function salvar(publicar: boolean) {
    if (salvando) return;
    setErro(null);
    setAvisoSalvo(false);

    if (modo === 'evento' && !eventoId) {
      setErro('Escolha de qual evento é a ação entre amigos para continuar.');
      return;
    }

    setSalvando(true);
    try {
      let idAtual = rifaId;

      // Só cria a rifa na primeira vez; nas próximas, reaproveita o mesmo id.
      if (idAtual === null) {
        const urls = await processarPremios();

        const premiosPayload = premios.map((p, i) => ({
          descricao: p.desc.trim() || 'Prêmio',
          doado_por: p.doou.trim() || null,
          imagem_url: urls[i] || null,
        }));

        const { data: r, error: errCriar } = await supabase.rpc('criar_rifa_completa', {
          p: {
            modo,
            evento_beneficiado_id: eventoId ?? null,
            nome: nomeRifa.trim() || null,
            meta_reais: metaCentavos > 0 ? metaCentavos / 100 : null,
            descricao: descricao.trim() || null,
            cidade: cidade.trim() || null,
            uf: uf || null,
            premios: premiosPayload,
            sorteio_em: dataHoraSorteioISO(sorteioData, sorteioHora),
            sorteio_por: sorteioPor,
            sorteio_onde: sorteioOnde.trim() || null,
            valor_cota_reais: valorCentavos / 100,
            numeros_por_talao: Number(porTalao) || 0,
            max_por_compra: Number(maxCompra) || 0,
            qualquer_um_vende: quemVende === 'qualquer',
            aprovar_vendedores: quemVende === 'aprovar',
            entidade_nome: entidadeNome.trim() || null,
            entidade_cnpj: entidadeCnpj.trim() || null,
            autorizacao_spa: autorizacaoSpa.trim() || null,
            regulamento: regulamento.trim() || null,
          },
        });

        if (errCriar) {
          setErro(mensagemAmigavel(errCriar, 'Não foi possível criar a ação entre amigos.'));
          return;
        }

        const resultado = r as { rifa_id: number } | null;
        if (!resultado?.rifa_id) {
          setErro('A ação entre amigos foi criada, mas não conseguimos o identificador. Tente de novo.');
          return;
        }
        idAtual = resultado.rifa_id;
        setRifaId(idAtual);
        localStorage.setItem('rascunho:rifa', String(idAtual));
      }

      // Persiste as alterações (limite, máximo por compra, sorteio e o bloco legal).
      const ajuste: Record<string, unknown> = {
        regulamento: regulamento.trim() || null,
        sorteio_onde: sorteioOnde.trim() || null,
        entidade_nome: entidadeNome.trim() || null,
        entidade_cnpj: entidadeCnpj.trim() || null,
        autorizacao_spa: autorizacaoSpa.trim() || null,
        taxa_por_dentro: absorvoATaxa,
      };
      const limiteNum = Number(limite);
      if (limite.trim() !== '' && Number.isFinite(limiteNum) && limiteNum > 0) {
        ajuste.max_taloes_por_pessoa = limiteNum;
      }
      const maxCompraNum = Number(maxCompra);
      if (Number.isFinite(maxCompraNum) && maxCompraNum > 0) {
        ajuste.max_por_compra = maxCompraNum;
      }

      const { error: errAjuste } = await supabase.rpc('ajustar_rifa', {
        p_rifa: idAtual,
        p: ajuste,
      });
      if (errAjuste) {
        setErro(mensagemAmigavel(errAjuste, 'Não foi possível salvar as alterações.'));
        return;
      }

      if (publicar) {
        const { error: errPublicar } = await supabase.rpc('publicar_rifa', {
          p_rifa: idAtual,
        });
        if (errPublicar) {
          setErro(mensagemAmigavel(errPublicar, 'Não foi possível publicar. Veja o que falta.'));
          return;
        }

        if (modo === 'evento') {
          await supabase.rpc('repartir_taloes', { p_rifa: idAtual });
        }

        navigate(`/sorteio/${idAtual}/pronta`);
      } else {
        setAvisoSalvo(true);
      }
    } catch (e) {
      setErro(
        mensagemAmigavel(e, 'Não foi possível salvar. Tente novamente.'),
      );
    } finally {
      setSalvando(false);
    }
  }

  const porTalaoNum = Number(porTalao) || 0;
  const taxaCentavos = Math.ceil(valorCentavos * 0.07);
  const camposLegaisPreenchidos =
    entidadeNome.trim() !== '' && entidadeCnpj.trim() !== '' && autorizacaoSpa.trim() !== '';

  const caixaRoxa = (
    <CaixaRoxa
      modo={modo}
      valorCentavos={valorCentavos}
      porTalao={porTalaoNum}
      inscritos={inscritos}
      valorInscricaoCentavos={valorInscricaoCentavos}
      metaCentavos={metaCentavos}
      absorvoATaxa={absorvoATaxa}
    />
  );

  if (carregando) {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="mx-auto w-full max-w-[880px] px-4 py-8">
          <div className="h-8 w-48 animate-pulse rounded bg-secondary-100" />
          <div className="mt-6 h-64 w-full animate-pulse rounded-lg bg-secondary-100" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-100">
      <header className="fixed inset-x-0 top-0 z-20 border-b border-secondary-200 bg-white">
        <div className="mx-auto w-full max-w-[1080px] px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <p className="min-w-0 truncate text-sm font-medium text-foreground-950">
              Nova ação entre amigos
            </p>
            <button
              type="button"
              onClick={sair}
              className="cursor-pointer whitespace-nowrap text-sm text-secondary-500 transition-colors hover:text-foreground-800"
            >
              Sair
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[1080px] px-4 pb-40 pt-24">
        <h1 className="text-[28px] font-normal text-foreground-950">Criar uma ação entre amigos</h1>
        <p className="mt-1 text-sm text-secondary-500">
          Preencha os blocos abaixo. A conta ao lado refaz sozinha a cada mudança.
        </p>

        {erro && (
          <div className="mt-4 rounded-md border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
            {erro}
            <button
              type="button"
              onClick={() => setErro(null)}
              className="ml-2 cursor-pointer font-medium underline"
            >
              Fechar
            </button>
          </div>
        )}

        <div className="mt-6 lg:grid lg:grid-cols-[1fr_360px] lg:items-start lg:gap-8">
          {/* Coluna dos blocos */}
          <div className="min-w-0 space-y-6">
            {/* BLOCO 1 · Para que serve */}
            <section className="overflow-hidden rounded-lg border border-secondary-200 bg-white">
              <div className="h-[10px] w-full bg-primary-500" />
              <div className="p-6">
                <h2 className="text-base font-medium text-foreground-950">
                  Para que serve esta ação entre amigos?
                </h2>
                {eventoDaUrl ? (
                  <div className="mt-4 flex items-start gap-2 rounded-md bg-primary-50 px-3 py-2.5">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-primary-600">
                      <i className="ri-lock-line text-base" />
                    </span>
                    <p className="text-sm text-primary-700">
                      Esta ação entre amigos banca um evento. Você veio pela aba Arrecadar, então ele já
                      está ligado a ele.
                    </p>
                  </div>
                ) : (
                  <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={() => setModo('evento')}
                      className={`cursor-pointer rounded-lg border p-4 text-left transition-colors ${
                        modo === 'evento'
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-secondary-200 bg-white hover:border-primary-300'
                      }`}
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                        <i className="ri-ticket-2-line text-lg" />
                      </span>
                      <span className="mt-3 block text-[15px] font-semibold text-foreground-950">
                        Ajudar a pagar as inscrições de um evento
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setModo('obra')}
                      className={`cursor-pointer rounded-lg border p-4 text-left transition-colors ${
                        modo === 'obra'
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-secondary-200 bg-white hover:border-primary-300'
                      }`}
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                        <i className="ri-home-2-line text-lg" />
                      </span>
                      <span className="mt-3 block text-[15px] font-semibold text-foreground-950">
                        Arrecadar para uma obra ou causa
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </section>

            {/* BLOCO 2A · De qual evento */}
            {modo === 'evento' && (
              <section className="overflow-hidden rounded-lg border border-secondary-200 bg-white">
                <div className="h-[10px] w-full bg-primary-500" />
                <div className="p-6">
                  <h2 className="text-base font-medium text-foreground-950">De qual evento</h2>
                  <p className="mt-0.5 text-sm text-secondary-500">
                    O dinheiro ajuda a quitar as inscrições deste evento.
                  </p>
                  <div className="mt-3">
                    {eventoDaUrl ? (
                      <div className="flex items-center gap-2 rounded-md border border-secondary-200 bg-background-50 px-3 py-2.5">
                        <span className="flex h-4 w-4 shrink-0 items-center justify-center text-secondary-400">
                          <i className="ri-lock-line" />
                        </span>
                        <span className="text-[15px] text-foreground-900">
                          {eventoNome || 'Evento selecionado'}
                        </span>
                      </div>
                    ) : (
                      <select
                        value={eventoId ? String(eventoId) : ''}
                        onChange={(e) => aoEscolherEvento(e.target.value)}
                        className={`${inputCls} cursor-pointer`}
                      >
                        <option value="">Selecione um evento</option>
                        {eventos.map((ev) => (
                          <option key={ev.id} value={ev.id}>
                            {ev.nome}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  {eventos.length === 0 && !eventoDaUrl && (
                    <p className="mt-2 text-sm text-secondary-500">
                      Você não tem eventos em andamento que ainda não vendem ingressos.
                    </p>
                  )}
                </div>
              </section>
            )}

            {/* BLOCO 2B · A obra */}
            {modo === 'obra' && (
              <section className="overflow-hidden rounded-lg border border-secondary-200 bg-white">
                <div className="h-[10px] w-full bg-primary-500" />
                <div className="space-y-5 p-6">
                  <h2 className="text-base font-medium text-foreground-950">A obra</h2>

                  <CampoTexto
                    id="nome-rifa"
                    name="nome-rifa"
                    label="Nome da ação entre amigos"
                    help="Ex.: Ação entre amigos da Reforma do Salão."
                    value={nomeRifa}
                    onChange={setNomeRifa}
                    placeholder="Ação entre amigos da Reforma do Salão"
                  />

                  <CampoDinheiro
                    id="meta"
                    name="meta"
                    label="Meta de arrecadação (R$)"
                    centavos={metaCentavos}
                    onChange={setMetaCentavos}
                    placeholder="10.000,00"
                  />

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <CampoTexto
                      id="cidade-rifa"
                      name="cidade-rifa"
                      label="Cidade"
                      value={cidade}
                      onChange={setCidade}
                      placeholder="Tupã"
                    />
                    <div>
                      <label htmlFor="uf-rifa" className="block text-base font-medium text-foreground-950">
                        Estado
                      </label>
                      <select
                        id="uf-rifa"
                        value={uf}
                        onChange={(e) => setUf(e.target.value)}
                        className={`${inputCls} cursor-pointer`}
                      >
                        <option value="">Selecione</option>
                        {UFS.map((u) => (
                          <option key={u} value={u}>
                            {u}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <CampoArea
                    id="para-que"
                    name="para-que"
                    label="Para que é o dinheiro"
                    help="Explique o que a arrecadação vai pagar."
                    value={descricao}
                    onChange={setDescricao}
                    placeholder="Reforma do telhado, compra de bancos novos…"
                  />
                </div>
              </section>
            )}

            {/* BLOCO 3 · Os prêmios */}
            <BlocoPremios premios={premios} onChange={setPremios} />

            {/* BLOCO 4 · O sorteio */}
            <section className="overflow-hidden rounded-lg border border-secondary-200 bg-white">
              <div className="h-[10px] w-full bg-primary-500" />
              <div className="space-y-5 p-6">
                <h2 className="text-base font-medium text-foreground-950">A apuração</h2>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-base font-medium text-foreground-950">
                      Data e hora
                    </label>
                    <div className="mt-1 flex gap-2">
                      <input
                        type="date"
                        value={sorteioData}
                        onChange={(e) => setSorteioData(e.target.value)}
                        className={`${inputCls} cursor-pointer`}
                      />
                      <input
                        type="time"
                        value={sorteioHora}
                        onChange={(e) => setSorteioHora(e.target.value)}
                        className={`${inputCls} cursor-pointer`}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-base font-medium text-foreground-950">
                      Como vai ser a apuração
                    </label>
                    <select
                      value={sorteioPor}
                      onChange={(e) => setSorteioPor(e.target.value as SorteioPor)}
                      className={`${inputCls} mt-1 cursor-pointer`}
                    >
                      {OPCOES_SORTEIO.map((o) => (
                        <option key={o.valor} value={o.valor}>
                          {o.rotulo}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {(sorteioPor === 'instagram' || sorteioPor === 'facebook') && (
                  <CampoTexto
                    id="sorteio-onde"
                    name="sorteio-onde"
                    label={
                      sorteioPor === 'instagram'
                        ? '@ do perfil que vai fazer a live'
                        : '@ da página'
                    }
                    value={sorteioOnde}
                    onChange={setSorteioOnde}
                    placeholder="@nomeperfil"
                  />
                )}

                {sorteioPor === 'loteria_federal' && (
                  <p className="flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-amber-700">
                      <i className="ri-information-line text-lg" />
                    </span>
                    A extração é às quartas e sábados — a data acima tem que cair num desses dias.
                  </p>
                )}
              </div>
            </section>

            {/* BLOCO 5 · Os números */}
            <section className="overflow-hidden rounded-lg border border-secondary-200 bg-white">
              <div className="h-[10px] w-full bg-primary-500" />
              <div className="space-y-5 p-6">
                <h2 className="text-base font-medium text-foreground-950">Os números</h2>

                <CampoDinheiro
                  id="valor-numero"
                  name="valor-numero"
                  label="Valor de cada número (R$)"
                  centavos={valorCentavos}
                  onChange={setValorCentavos}
                  placeholder="10,00"
                />

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <CampoTexto
                    id="por-talao"
                    name="por-talao"
                    label="Números por talão"
                    value={porTalao}
                    onChange={setPorTalao}
                    inputMode="numeric"
                  />
                  <CampoTexto
                    id="max-compra"
                    name="max-compra"
                    label="Máximo por compra"
                    value={maxCompra}
                    onChange={setMaxCompra}
                    inputMode="numeric"
                  />
                </div>

                <CampoTexto
                  id="limite-taloes"
                  name="limite-taloes"
                  label="Quantos talões cada pessoa pode pegar"
                  help="Deixe vazio para não ter limite. A pessoa só recebe outro talão depois de vender o atual, sempre."
                  value={limite}
                  onChange={setLimite}
                  inputMode="numeric"
                />

                {modo === 'obra' && (
                  <div>
                    <span className="block text-base font-medium text-foreground-950">
                      Quem pode pegar um talão para vender
                    </span>
                    <div className="mt-2 space-y-1">
                      <label className="flex cursor-pointer items-center gap-2">
                        <input
                          type="radio"
                          name="quem-vende"
                          checked={quemVende === 'qualquer'}
                          onChange={() => setQuemVende('qualquer')}
                          className="h-4 w-4 cursor-pointer text-primary-500"
                        />
                        <span className="text-[15px] text-foreground-900">
                          Qualquer pessoa que entrar na página
                        </span>
                      </label>
                      <label className="flex cursor-pointer items-center gap-2">
                        <input
                          type="radio"
                          name="quem-vende"
                          checked={quemVende === 'aprovar'}
                          onChange={() => setQuemVende('aprovar')}
                          className="h-4 w-4 cursor-pointer text-primary-500"
                        />
                        <span className="text-[15px] text-foreground-900">
                          Só quem eu aprovar
                        </span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Caixa roxa (mobile — logo abaixo dos números) */}
            <div className="lg:hidden">{caixaRoxa}</div>

            {/* BLOCO 6 · A taxa da plataforma (escolha) */}
            <section className="overflow-hidden rounded-lg border border-secondary-200 bg-white">
              <div className="h-[10px] w-full bg-primary-500" />
              <div className="p-6">
                <h2 className="text-base font-medium text-foreground-950">
                  A taxa da plataforma
                </h2>
                <p className="mt-1 text-sm text-secondary-600">
                  A plataforma fica com 7% de cada número vendido pelo site. Número vendido no
                  papel e pago em dinheiro não paga taxa. Escolha quem cobre esses 7%:
                </p>

                <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
                  {/* Cartão A — Eu absorvo a taxa */}
                  <button
                    type="button"
                    onClick={() => setAbsorvoATaxa(true)}
                    className={`cursor-pointer rounded-lg border p-4 text-left transition-colors ${
                      absorvoATaxa
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-secondary-200 bg-white hover:border-primary-300'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span
                        className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                          absorvoATaxa ? 'border-primary-500' : 'border-secondary-300'
                        }`}
                      >
                        {absorvoATaxa && (
                          <span className="h-2 w-2 rounded-full bg-primary-500" />
                        )}
                      </span>
                      <span className="text-[15px] font-semibold text-foreground-950">
                        Eu absorvo a taxa
                      </span>
                    </span>
                    <span className="mt-1 block text-sm text-secondary-600">
                      O comprador paga o valor redondo. Recomendado para ação entre amigos.
                    </span>

                    {valorCentavos > 0 && (
                      <span className="mt-3 block rounded-md bg-background-100 px-3 py-2.5">
                        <span className="flex items-baseline justify-between gap-4 text-sm">
                          <span className="text-secondary-600">O comprador paga</span>
                          <span className="whitespace-nowrap text-foreground-900">
                            {formatarCentavos(valorCentavos)}
                          </span>
                        </span>
                        <span className="mt-1.5 flex items-baseline justify-between gap-4 text-sm">
                          <span className="text-secondary-600">A obra recebe</span>
                          <span className="whitespace-nowrap text-foreground-900">
                            {formatarCentavos(valorCentavos - taxaCentavos)}
                          </span>
                        </span>
                      </span>
                    )}
                  </button>

                  {/* Cartão B — O comprador paga a taxa */}
                  <button
                    type="button"
                    onClick={() => setAbsorvoATaxa(false)}
                    className={`cursor-pointer rounded-lg border p-4 text-left transition-colors ${
                      !absorvoATaxa
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-secondary-200 bg-white hover:border-primary-300'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span
                        className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                          !absorvoATaxa ? 'border-primary-500' : 'border-secondary-300'
                        }`}
                      >
                        {!absorvoATaxa && (
                          <span className="h-2 w-2 rounded-full bg-primary-500" />
                        )}
                      </span>
                      <span className="text-[15px] font-semibold text-foreground-950">
                        O comprador paga a taxa
                      </span>
                    </span>
                    <span className="mt-1 block text-sm text-secondary-600">
                      A obra recebe o valor cheio, o comprador paga um pouco a mais.
                    </span>

                    {valorCentavos > 0 && (
                      <span className="mt-3 block rounded-md bg-background-100 px-3 py-2.5">
                        <span className="flex items-baseline justify-between gap-4 text-sm">
                          <span className="text-secondary-600">Número da ação entre amigos</span>
                          <span className="whitespace-nowrap text-foreground-900">
                            {formatarCentavos(valorCentavos)}
                          </span>
                        </span>
                        <span className="mt-1.5 flex items-baseline justify-between gap-4 text-sm">
                          <span className="text-secondary-600">Taxas da operação</span>
                          <span className="whitespace-nowrap text-foreground-900">
                            {formatarCentavos(taxaCentavos)}
                          </span>
                        </span>
                        <span className="mt-1.5 flex items-baseline justify-between gap-4 text-sm">
                          <span className="text-secondary-600">O comprador paga</span>
                          <span className="whitespace-nowrap text-foreground-900">
                            {formatarCentavos(valorCentavos + taxaCentavos)}
                          </span>
                        </span>
                        <span className="mt-1.5 flex items-baseline justify-between gap-4 border-t border-secondary-200 pt-1.5 text-sm">
                          <span className="font-medium text-foreground-950">A obra recebe</span>
                          <span className="whitespace-nowrap font-medium text-foreground-950">
                            {formatarCentavos(valorCentavos)}
                          </span>
                        </span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </section>

            {/* BLOCO 7 · O que a lei exige */}
            <section className="overflow-hidden rounded-lg border border-secondary-200 bg-white border-l-4 border-l-amber-400">
              <div className="p-6">
                <h2 className="text-base font-medium text-foreground-950">O que a lei exige</h2>
                <p className="mt-1 text-sm text-secondary-600">
                  Ação entre amigos sem autorização é contravenção (art. 51 do Decreto-Lei 3.688/41) e quem
                  responde é a pessoa física que organizou. Sem esses três campos a ação entre amigos salva
                  como rascunho, mas não publica.
                </p>

                <div className="mt-5 space-y-5">
                  <CampoTexto
                    id="entidade-nome"
                    name="entidade-nome"
                    label="Entidade responsável"
                    value={entidadeNome}
                    onChange={setEntidadeNome}
                    placeholder="Paróquia São José"
                  />
                  <CampoTexto
                    id="entidade-cnpj"
                    name="entidade-cnpj"
                    label="CNPJ"
                    value={entidadeCnpj}
                    onChange={(v) => setEntidadeCnpj(mascaraCNPJ(v))}
                    inputMode="numeric"
                    placeholder="00.000.000/0000-00"
                  />
                  <CampoTexto
                    id="autorizacao"
                    name="autorizacao"
                    label="Nº da autorização (SPA/Ministério da Fazenda)"
                    value={autorizacaoSpa}
                    onChange={setAutorizacaoSpa}
                    placeholder="0000000/0000"
                  />
                  <CampoArea
                    id="regulamento"
                    name="regulamento"
                    label="Regulamento"
                    value={regulamento}
                    onChange={setRegulamento}
                  />
                </div>
              </div>
            </section>
          </div>

          {/* Caixa roxa (desktop — fixa à direita) */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">{caixaRoxa}</div>
          </aside>
        </div>
      </main>

      {/* Rodapé fixo */}
      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-secondary-200 bg-white px-4 py-3">
        <div className="mx-auto w-full max-w-[1080px]">
          {avisoSalvo && (
            <p className="mb-2 flex items-center gap-1.5 text-sm font-medium text-green-700">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-checkbox-circle-line" />
              </span>
              Rascunho salvo
            </p>
          )}
          <p
            className={`mb-2 text-sm ${
              camposLegaisPreenchidos ? 'text-green-700' : 'text-amber-700'
            }`}
          >
            {camposLegaisPreenchidos
              ? 'Tudo certo. A ação entre amigos pode ser publicada.'
              : 'Preencha entidade, CNPJ e autorização para poder publicar.'}
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
            <button
              type="button"
              onClick={() => salvar(false)}
              disabled={salvando}
              className="cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-white px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {rifaId === null ? 'Salvar rascunho' : 'Salvar alterações'}
            </button>
            <button
              type="button"
              onClick={() => salvar(true)}
              disabled={salvando || !camposLegaisPreenchidos}
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-secondary-200 disabled:text-secondary-400"
            >
              {salvando ? 'Salvando…' : 'Publicar ação entre amigos'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}